import { Disclosure, type ScrubProps } from '@/components/builder/inspector-controls';
import { sizeOptions, type SizeMode, type StepCtx } from '@/components/builder/validate';
import { cn } from '@/lib/cn';
import type { BlockNode, Padding, Size, SizeVal, Span, Theme } from '@personalizehq/schemas/ir';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';

// The inspector's widgets. Native `<select>`/`<input>` rather than Radix: this panel is DENSE (a
// `video` has nine fields), the natives are keyboard- and screen-reader-correct for free, and they
// need no pointer-capture shim to be testable. shadcn stays for the roomy CRUD pages.

/** THE row. A fixed 84px label column (Plasmic's `$label-col-width`), a "set here" dot, right-click
 *  to unset, and an optional scrub handle on the label itself.
 *
 *  The dot is BINARY for us. Plasmic's `DefinedIndicator` needs eight semantic colours because it
 *  has variants, mixins and themes to disambiguate; we have none of those, so a value is either
 *  present on this node or it is absent — and that is also what `Disclosure` keys on to decide
 *  whether the row appears at all. */
export function Field({
  label,
  error,
  set,
  onUnset,
  scrub,
  children,
}: {
  label: string;
  error?: string | undefined;
  /** Is the key actually PRESENT on the node? Drives the dot, and drives `Disclosure`. */
  set?: boolean | undefined;
  onUnset?: (() => void) | undefined;
  scrub?: ScrubProps | undefined;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      data-row=""
      className="space-y-1"
      onContextMenu={
        onUnset &&
        ((e) => {
          e.preventDefault();
          onUnset();
        })
      }
    >
      <div className="flex items-center gap-2">
        <label
          {...scrub}
          title={scrub ? 'drag to change' : undefined}
          className={cn(
            'flex w-[84px] shrink-0 items-center gap-1 text-[11px] text-muted-foreground',
            scrub && 'cursor-ew-resize select-none'
          )}
        >
          <span className={cn('size-1 shrink-0 rounded-full', set ? 'bg-indigo-400' : 'bg-transparent')} />
          {label}
        </label>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1">{children}</div>
      </div>
      {/* The law is surfaced AT THE FIELD, the moment it is wrong — never as a list of 14 errors
          after clicking publish (design hand-off §6). */}
      {error ? <p className="text-right text-[11px] text-destructive">{error}</p> : null}
    </div>
  );
}

const CONTROL =
  'h-7 min-w-0 rounded-md border bg-transparent px-1.5 text-[12px] outline-none focus:border-indigo-500';

export function TextInput({
  value,
  onChange,
  className,
  ...props
}: {
  value: string;
  onChange: (value: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>): React.JSX.Element {
  return (
    <input
      {...props}
      className={cn(CONTROL, 'w-[150px]', className)}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function NumberInput({
  value,
  onChange,
  className,
  ...props
}: {
  /** `undefined` renders empty and means "the field is absent" — not zero. */
  value: number | undefined;
  onChange: (value: number | undefined) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>): React.JSX.Element {
  return (
    <input
      {...props}
      type="number"
      className={cn(CONTROL, 'w-[70px]', className)}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
    />
  );
}

export function SelectInput<T extends string>({
  value,
  options,
  labels,
  onChange,
  className,
  ...props
}: {
  value: T;
  options: readonly T[];
  /** DISPLAY ONLY. The wire values are pinned in a binary and cannot be renamed — but `fit: "fill"`
   *  (stretch the pixels) sits three rows from a size `fill` (take the leftover space), and reading
   *  the raw enum is how an author comes to believe `cover` should push a button down the screen.
   *  The option's `value` is untouched, so anything walking this control by value still finds it. */
  labels?: Partial<Record<T, string>> | undefined;
  onChange: (value: T) => void;
  className?: string;
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'>): React.JSX.Element {
  return (
    <select
      {...props}
      className={cn(CONTROL, 'w-[150px]', className)}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {labels?.[option] ?? option}
        </option>
      ))}
    </select>
  );
}

export function BoolInput({
  value,
  onChange,
  label,
}: {
  value: boolean | undefined;
  onChange: (value: boolean) => void;
  label: string;
}): React.JSX.Element {
  return (
    <label className="flex items-center gap-1.5 text-[12px]">
      <input
        type="checkbox"
        aria-label={label}
        checked={value ?? false}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

// ---------------------------------------------------------------------------- tokens

const NONE = '__none__';

/** THE signature input. A colour is a token, never a hex — a raw value has no dark variant, so the
 *  wire forbids it (SPEC §10) and the picker IS the input rather than a convenience over one. The
 *  swatch shows BOTH halves of the pair, because that is what the token actually is. */
export function TokenPicker({
  label,
  kind,
  theme,
  value,
  onChange,
  optional,
}: {
  /** REQUIRED. An unnamed `<select>` announces nothing to a screen reader, and tells the control
   *  inventory nothing about which control went missing when one does. */
  label: string;
  kind: 'colors' | 'fonts' | 'buttons';
  theme: Theme;
  value: string | null | undefined;
  onChange: (value: string | undefined) => void;
  optional?: boolean;
}): React.JSX.Element {
  const keys = Object.keys(theme[kind]);
  const current = value ?? NONE;

  return (
    <span className="flex items-center gap-1.5">
      {kind === 'colors' && value != null && theme.colors[value] ? (
        <span className="flex size-4 shrink-0 overflow-hidden rounded-full border">
          <span className="w-1/2" style={{ background: theme.colors[value].light }} />
          <span className="w-1/2" style={{ background: theme.colors[value].dark }} />
        </span>
      ) : null}
      <select
        aria-label={label}
        className={cn(CONTROL, 'w-[130px]')}
        value={current}
        onChange={(e) => onChange(e.target.value === NONE ? undefined : e.target.value)}
      >
        {optional || !keys.includes(current) ? <option value={NONE}>{optional ? 'none' : '—'}</option> : null}
        {keys.map((key) => (
          <option key={key} value={key}>
            {key}
          </option>
        ))}
      </select>
    </span>
  );
}

// ---------------------------------------------------------------------------- size

const modeOf = (value: SizeVal | undefined): SizeMode =>
  value === undefined ? 'auto' : value === 'fill' || value === 'hug' ? value : 'fixed';

/** The legality of every option here is PROBED against `blockSchema` by `sizeOptions()` — this
 *  widget only renders the answer. It used to hand-copy the five size schemas (`modes()`/
 *  `canClear()`), which is how it came to offer values that could not publish. */
export function SizeField({
  node,
  parent,
  step,
  size,
  onChange,
  errors,
}: {
  node: BlockNode;
  parent: BlockNode | null;
  /** The step this node lives in. The ROOT's height law needs the SURFACE, and only the document can
   *  see the surface and the root together (SPEC §6.1c). */
  step?: StepCtx | undefined;
  size: Size | undefined;
  /** `field` names the AXIS that changed, and it becomes the undo merge key. Without it every size
   *  edit shares one key, so setting a width and then a height coalesce into a SINGLE undo entry and
   *  ⌘Z jumps back past both. Two deliberate edits are two undo steps. */
  onChange: (size: Size | undefined, field?: string) => void;
  errors: {
    width?: string | undefined;
    height?: string | undefined;
    max_width?: string | undefined;
    min_width?: string | undefined;
  };
}): React.JSX.Element {
  const legality = sizeOptions(node, parent, step);

  const axis = (name: 'width' | 'height'): React.JSX.Element => {
    const value = name === 'width' ? size?.width : size?.height;
    const options = legality[name];

    const set = (next: SizeVal | undefined): void => {
      // Clearing the WIDTH clears the whole object — `width` is the only non-optional member of
      // `Size`. That is only ever reachable when nothing else is set, because `sizeOptions` probes
      // the same candidate and stops offering `auto` the moment a height exists (it would delete it).
      if (name === 'width') {
        if (next === undefined) return onChange(undefined, `size.${name}`);
        return onChange({ ...size, width: next }, `size.${name}`);
      }
      // SPREAD, then delete. This used to rebuild `{width, height}` from scratch, which silently
      // dropped every other member of Size — today that is max_width/min_width, tomorrow whatever
      // else lands here.
      const patch: Size = { ...size, width: size?.width ?? 'fill', height: next };
      if (next === undefined) delete patch.height;
      onChange(patch, `size.${name}`);
    };

    return (
      <Field key={name} label={name} error={errors[name]} set={value !== undefined}>
        <SelectInput
          className="w-[80px]"
          aria-label={`${name} mode`}
          value={modeOf(value)}
          options={options}
          onChange={(mode) =>
            set(mode === 'auto' ? undefined : mode === 'fixed' ? (typeof value === 'number' ? value : 100) : mode)
          }
        />
        {typeof value === 'number' ? (
          <NumberInput
            className="w-[62px]"
            aria-label={name}
            min={0}
            value={value}
            onChange={(n) => set(n ?? 0)}
          />
        ) : null}
      </Field>
    );
  };

  /** A bound on the RESOLVED width (SPEC §6.1b). Offered only when the schema says it may be — it
   *  rejects a bound against a fixed width as a typo (you already said what the width is), and an
   *  input that only ever produces an unpublishable document is worse than no input. */
  const bound = (name: 'max_width' | 'min_width'): React.JSX.Element | null => {
    if (!legality.bounds || !size) return null;
    return (
      <Field key={name} label={name.replace('_', ' ')} error={errors[name]} set={size[name] !== undefined}>
        <NumberInput
          className="w-[62px]"
          aria-label={name.replace('_', ' ')}
          min={0}
          value={size[name]}
          onChange={(n) => {
            const patch: Size = { ...size, [name]: n };
            if (n === undefined) delete patch[name];
            onChange(patch, `size.${name}`);
          }}
        />
      </Field>
    );
  };

  // `max_width` and `min_width` DO NOT EXIST ON SCREEN until you have used them. Plasmic's
  // `SizeSection` is the canonical case for progressive disclosure and it is exactly ours: two rows
  // that almost nobody sets, sitting in everybody's panel forever.
  return (
    <>
      {axis('width')}
      {axis('height')}
      <Disclosure>
        {bound('max_width')}
        {bound('min_width')}
      </Disclosure>
    </>
  );
}

// ---------------------------------------------------------------------------- padding

const SIDES = ['top', 'end', 'bottom', 'start'] as const;

export function PaddingField({
  padding,
  onChange,
}: {
  padding: Padding | undefined;
  onChange: (padding: Padding | undefined) => void;
}): React.JSX.Element {
  const current: Padding = padding ?? { top: 0, end: 0, bottom: 0, start: 0 };
  const all = (patch: Partial<Padding>): void => {
    const next = { ...current, ...patch };
    // All-zero padding IS no padding — keeping `{0,0,0,0}` on every block would bloat every bundle
    // with a field that means nothing.
    onChange(SIDES.every((s) => next[s] === 0) ? undefined : next);
  };

  return <PaddingBox current={current} padding={padding} all={all} />;
}

/** The box widget: four inputs AROUND a centre cell, so the control looks like the thing it edits.
 *  The centre links all four — the overwhelmingly common case, and four loose boxes in a row made
 *  the author do it by hand every time.
 *
 *  `start`/`end`, never `left`/`right`: padding MIRRORS under RTL (SPEC §5.1), so naming it by
 *  screen side would be a lie on half the world's devices. The box places them where they land in
 *  LTR and says `start`/`end` out loud in the label. */
function PaddingBox({
  current,
  padding,
  all,
}: {
  current: Padding;
  padding: Padding | undefined;
  all: (patch: Partial<Padding>) => void;
}): React.JSX.Element {
  const [linked, setLinked] = useState(
    padding !== undefined && SIDES.every((s) => current[s] === current.top)
  );

  const write = (side: (typeof SIDES)[number], n: number | undefined): void =>
    all(linked ? { top: n ?? 0, end: n ?? 0, bottom: n ?? 0, start: n ?? 0 } : { [side]: n ?? 0 });

  const cell = (side: (typeof SIDES)[number]): React.JSX.Element => (
    <NumberInput
      className="w-full"
      aria-label={`padding ${side}`}
      min={0}
      value={current[side]}
      onChange={(n) => write(side, n)}
    />
  );

  return (
    <Field label="padding" set={padding !== undefined} onUnset={() => all({ top: 0, end: 0, bottom: 0, start: 0 })}>
      <div className="grid w-[150px] grid-cols-3 gap-1">
        <span />
        {cell('top')}
        <span />
        {cell('start')}
        <button
          type="button"
          role="checkbox"
          aria-checked={linked}
          aria-label="link padding"
          title={linked ? 'editing all four sides' : 'link all four sides'}
          onClick={() => {
            const next = !linked;
            setLinked(next);
            if (next) all({ top: current.top, end: current.top, bottom: current.top, start: current.top });
          }}
          className={cn(
            'flex h-7 items-center justify-center rounded-md border text-[11px]',
            linked ? 'border-indigo-500 text-indigo-400' : 'text-muted-foreground'
          )}
        >
          {linked ? '⊞' : '⊟'}
        </button>
        {cell('end')}
        <span />
        {cell('bottom')}
        <span />
      </div>
    </Field>
  );
}

// ---------------------------------------------------------------------------- spans

/** Spans, not a template string. `{{path}}` would force four mini-parsers into pinned binaries
 *  (SPEC §11.1) — so a `ref` is a structured span with a REQUIRED fallback, and the editor asks for
 *  it at the moment the span is created. Inline canvas editing with variable chips is E3; this list
 *  is the E1 surface for exactly the same data. */
export function SpansField({
  spans,
  onChange,
  errors,
}: {
  spans: Span[];
  onChange: (spans: Span[]) => void;
  errors: Record<string, string>;
}): React.JSX.Element {
  const replace = (index: number, span: Span): void =>
    onChange(spans.map((s, i) => (i === index ? span : s)));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">spans</span>
        <span className="flex gap-1">
          <button
            type="button"
            className="rounded border px-1.5 py-0.5 text-[11px] hover:bg-secondary"
            onClick={() => onChange([...spans, { kind: 'lit', value: 'Text' }])}
          >
            + text
          </button>
          <button
            type="button"
            className="rounded border px-1.5 py-0.5 text-[11px] hover:bg-secondary"
            onClick={() => onChange([...spans, { kind: 'ref', path: 'user.name', fallback: 'there' }])}
          >
            + variable
          </button>
        </span>
      </div>

      {spans.map((span, index) => (
        <div key={index} className="space-y-1 rounded-md border p-1.5">
          <div className="flex items-center gap-1">
            {span.kind === 'lit' ? (
              <TextInput
                className="flex-1"
                aria-label={`span ${index} text`}
                value={span.value}
                onChange={(value) => replace(index, { kind: 'lit', value })}
              />
            ) : (
              <TextInput
                className="flex-1"
                aria-label={`span ${index} path`}
                value={span.path}
                onChange={(path) => replace(index, { ...span, path })}
              />
            )}
            <button
              type="button"
              aria-label={`Remove span ${index}`}
              className="p-1 text-muted-foreground hover:text-destructive disabled:opacity-30"
              disabled={spans.length === 1}
              onClick={() => onChange(spans.filter((_, i) => i !== index))}
            >
              <X className="size-3" />
            </button>
          </div>

          {span.kind === 'ref' ? (
            <Field label="fallback" error={errors[`spans.${index}.fallback`]}>
              <TextInput
                className="w-[130px]"
                aria-label={`span ${index} fallback`}
                value={span.fallback}
                onChange={(fallback) => replace(index, { ...span, fallback })}
              />
            </Field>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------- custom config

/** The host's own struct — we hand it the raw blob and never type it (SPEC §8). Parsed on every
 *  keystroke but only COMMITTED when it parses, so a half-typed brace does not blank the block. */
export function ConfigField({
  config,
  onChange,
}: {
  config: Record<string, unknown> | undefined;
  onChange: (config: Record<string, unknown> | undefined) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState(() => (config ? JSON.stringify(config, null, 2) : ''));
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <div className="space-y-1">
      <label className="text-[11px] text-muted-foreground" htmlFor="config">
        config (JSON)
      </label>
      <textarea
        id="config"
        aria-label="config"
        rows={4}
        className="w-full rounded-md border bg-transparent p-1.5 font-mono text-[11px] outline-none focus:border-indigo-500"
        value={draft}
        onChange={(e) => {
          const next = e.target.value;
          setDraft(next);
          if (next.trim() === '') {
            setError(undefined);
            return onChange(undefined);
          }
          try {
            const parsed: unknown = JSON.parse(next);
            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
              return setError('config must be a JSON object');
            }
            setError(undefined);
            onChange(parsed as Record<string, unknown>);
          } catch {
            setError('invalid JSON');
          }
        }}
      />
      {error ? <p className="text-[11px] text-destructive">{error}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------- requires

/** The capability gate (SPEC §11.5). An unsatisfied `requires` sends the block to its `fallback` —
 *  which is why the inspector nags for one when this is set. */
export function RequiresField({
  requires,
  onChange,
  set,
}: {
  requires: string[] | undefined;
  onChange: (requires: string[] | undefined) => void;
  /** Passed in rather than derived, because `Disclosure` reads it off THIS element's props to
   *  decide whether the row is on screen at all — it cannot see inside the component. */
  set?: boolean;
}): React.JSX.Element {
  return (
    <Field label="requires" set={set}>
      <TextInput
        aria-label="requires"
        placeholder="video, lottie"
        value={(requires ?? []).join(', ')}
        onChange={(value) => {
          const list = value
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          onChange(list.length > 0 ? list : undefined);
        }}
      />
    </Field>
  );
}

export { Plus };
